// Позиция перечисления - со строчной буквы (СТО 4.4.3, 4.4.4: «текст начинать со строчной буквы»), кроме имён
// собственных и аббревиатур. Общие правила для живой проверки (LIST-CASE - ошибка, как у sto_check) и автозамены
// при наборе: в основном тексте и в малых редакторах (задание, реферат, раздел об ИИ).
//   isProperWord(word, doc)   - слово похоже на имя собственное / аббревиатуру (тогда прописную не трогаем)
//   ListCaseExtension         - после первого слова позиции («Включить » -> «включить ») - строчная буква;
//                               Backspace сразу после замены возвращает прописную (как автозамена Word)
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';

// имена собственные, с которых нередко начинают позицию перечисления в учебных работах (кириллица; латиница и
// аббревиатуры определяются по написанию)
const PROPER = new Set(('россия российская российской россии москва москве санкт-петербург петербург архангельск ' +
  'архангельске северодвинск северодвинске сафу мгу мгту минобрнауки росстат росстандарт гост госту ' +
  'интернет windows линукс линукса виндовс яндекс яндекса гугл майкрософт эксель ворд ' +
  'европа европы азия америка сша китай германия япония франция беларусь казахстан ' +
  'ленин петр пётр ломоносов ломоносова менделеев менделеева пушкин ньютон ньютона ом ома ампер вольт кельвин ' +
  'фурье лаплас лапласа найквист найквиста михайлов михайлова гурвиц гурвица раус рауса ляпунов ляпунова').split(/\s+/));

/** Слова, которые в текстах встречаются с прописной не в начале предложения (имена собственные). */
export function properWordsOfTexts(texts: string[], out: Set<string> = new Set<string>()): Set<string> {
  for (const t of texts) {
    const re = /[А-ЯЁ][а-яё]+/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(t))) {
      const before = t.slice(0, m.index).replace(/[\s\u00a0«"(]+$/, '');
      if (!before || /[.!?:;)]$/.test(before) || /^[-–—]$/.test(before) || /^[а-яa-z]\)$/i.test(before) || /^\d+\)$/.test(before)) continue;
      out.add(m[0].toLowerCase());
    }
  }
  return out;
}

/** То же по документу редактора. */
export function properWordsOf(doc: PMNode | null | undefined): Set<string> {
  const texts: string[] = [];
  doc?.descendants((n) => { if (!n.isTextblock) return true; texts.push(n.textContent); return false; });
  return properWordsOfTexts(texts);
}

/** Первое слово позиции - имя собственное / аббревиатура / латиница (прописную не трогаем)? */
export function isProperWord(word: string, rest: string, proper?: Set<string>): boolean {
  if (!/^[А-ЯЁ]/.test(word)) return true;                      // латиница, цифры - как набрано
  if (word.length === 1) return true;                          // «А» - буква-обозначение
  if (/[А-ЯЁ]/.test(word.slice(1))) return true;               // аббревиатура «САУ», «IPv4» / «ПИД-регулятор»
  const key = word.toLowerCase();
  if (PROPER.has(key)) return true;
  if (proper && proper.has(key)) return true;
  if (/^[\s ]*[А-ЯЁ]\.[\s ]?(?:[А-ЯЁ]\.)?/.test(rest)) return true;   // «Иванов И. И.» - фамилия
  return false;
}

const key = new PluginKey<{ last: { pos: number; ch: string } | null }>('stoListCase');

function inListItem(state: any, pos: number): boolean {
  const $p = state.doc.resolve(pos);
  if ($p.parent.type.name !== 'paragraph' || $p.depth < 2) return false;
  if ($p.node($p.depth - 1).type.name !== 'stoListItem') return false;
  return $p.index($p.depth - 1) === 0;          // первый абзац позиции
}

/** Автозамена: после первого слова позиции перечисления (пробел, запятая, точка с запятой) - строчная буква. */
export const ListCaseExtension = Extension.create({
  name: 'stoListCase',
  priority: 790,
  addProseMirrorPlugins() {
    return [new Plugin({
      key,
      state: {
        init: () => ({ last: null }),
        apply: (tr, prev) => {
          const meta = tr.getMeta(key);
          if (meta) return meta;
          return tr.docChanged || tr.selectionSet ? { last: null } : prev;
        },
      },
      appendTransaction: (trs, _old, state) => {
        const tr0 = trs[trs.length - 1];
        if (!trs.some((t) => t.docChanged) || !tr0 || tr0.getMeta(key) || tr0.getMeta('paste') || tr0.getMeta('uiEvent') === 'paste') return null;
        const sel = state.selection;
        if (!sel.empty || !inListItem(state, sel.from)) return null;
        const $f = sel.$from;
        const before = $f.parent.textBetween(0, $f.parentOffset, '\n', '￼');
        const m = before.match(/^([\s ]*)([А-ЯЁ][а-яё]+)([\s ,;:])$/);
        if (!m) return null;
        const word = m[2];
        if (isProperWord(word, '', properWordsOf(state.doc))) return null;
        const at = $f.start() + m[1].length;
        const tr = state.tr.insertText(word[0].toLowerCase(), at, at + 1);
        tr.setMeta(key, { last: { pos: at, ch: word[0] } });
        tr.setMeta('addToHistory', true);
        return tr;
      },
      props: {
        handleKeyDown: (view, ev) => {
          if (ev.key !== 'Backspace') return false;
          const st = key.getState(view.state);
          if (!st?.last) return false;
          const { pos, ch } = st.last;
          const tr = view.state.tr.insertText(ch, pos, pos + 1);
          tr.setMeta(key, { last: null });
          view.dispatch(tr);
          return true;
        },
      },
    })];
  },
});
